import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { CreditCard } from 'lucide-react';
import ReportDownload from '../ReportDownload';
import './MembershipAdmin.css';

/**
 * MembershipReport — Club Admin → Reports → "Membership & gate" (v35).
 *
 * One row per home fixture / gate day: members in, tickets by type, £ gate.
 * Then bar visits per week. Aggregate only — no names anywhere, by design
 * (the consent line: no per-person attendance history in v1).
 */
const gbp = (pence) => `£${(Number(pence || 0) / 100).toFixed(2).replace(/\.00$/, '')}`;
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

export default function MembershipReport({ clubSlug }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['membership-report', clubSlug, from, to],
    queryFn: () => axios.get(`/clubs/${clubSlug}/membership/report`, { params: { from: from || undefined, to: to || undefined } }).then(r => r.data),
  });

  const gate = data?.gate || [];
  const bar = data?.bar || [];
  const labels = [...new Set(gate.flatMap(g => g.tickets.map(t => t.label)))];
  const totals = gate.reduce((acc, g) => {
    acc.members += g.members_in; acc.tickets += g.tickets_total; acc.pence += g.gate_pence;
    for (const t of g.tickets) acc.byLabel[t.label] = (acc.byLabel[t.label] || 0) + t.count;
    return acc;
  }, { members: 0, tickets: 0, pence: 0, byLabel: {} });
  const barTotal = bar.reduce((n, w) => n + w.visits, 0);

  return (
    <div className="admin-section" data-tour="report-membership">
      <div className="admin-tab-head"><h2 className="admin-section-title"><CreditCard size={18} /> Membership &amp; gate</h2></div>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 4 }}>
        Club members through the gate, tickets sold to everyone else and gate money for every home
        fixture (or gate day), plus club-member visits to the bar each week. Totals only — no names.
      </p>
      <div className="form-row-2col" style={{ marginTop: 12 }}>
        <div className="form-group">
          <label className="label">From</label>
          <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="label">To</label>
          <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
      </div>

      {isLoading && <div className="page-loading" style={{ minHeight: 80 }}><div className="spinner" /></div>}
      {isError && <div className="madm-note is-error">{error?.response?.data?.error || 'Couldn\'t load the report.'}</div>}

      {data && (
        <>
          <div className="mrep-kpis">
            <div className="mrep-kpi"><strong>{totals.members}</strong><span>club members through the gate</span></div>
            <div className="mrep-kpi"><strong>{totals.tickets}</strong><span>gate tickets</span></div>
            <div className="mrep-kpi"><strong>{gbp(totals.pence)}</strong><span>gate money</span></div>
            <div className="mrep-kpi"><strong>{barTotal}</strong><span>bar visits</span></div>
          </div>

          {gate.length === 0 ? (
            <div className="empty-state" style={{ marginTop: 12 }}>
              Nothing on the gate yet{from || to ? ' in that period' : ''}. Rows appear the first time club staff scan a card or tap a ticket on a home match day.
            </div>
          ) : (
            <div className="mrep-table-wrap">
              <table className="mrep-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Fixture</th>
                    <th className="num">Club members in</th>
                    {labels.map(l => <th key={l} className="num">{l}</th>)}
                    <th className="num">Gate £</th>
                  </tr>
                </thead>
                <tbody>
                  {gate.map(g => (
                    <tr key={`${g.gate_date}-${g.event_id || 'day'}`}>
                      <td>{fmtDate(g.gate_date)}</td>
                      <td className="title">
                        {g.title}
                        {g.team_name && <div className="mrep-sub">{g.team_name}</div>}
                      </td>
                      <td className="num">{g.members_in}</td>
                      {labels.map(l => <td key={l} className="num">{(g.tickets.find(t => t.label === l) || { count: 0 }).count || '–'}</td>)}
                      <td className="num">{gbp(g.gate_pence)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>Total</td>
                    <td className="num">{totals.members}</td>
                    {labels.map(l => <td key={l} className="num">{totals.byLabel[l] || 0}</td>)}
                    <td className="num">{gbp(totals.pence)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {bar.length > 0 && (
            <div className="mrep-table-wrap" style={{ maxWidth: 360 }}>
              <table className="mrep-table" style={{ minWidth: 0 }}>
                <thead><tr><th>Bar — week starting</th><th className="num">Club-member visits</th></tr></thead>
                <tbody>
                  {bar.map(w => (
                    <tr key={w.week_start}><td>{fmtDate(w.week_start)}</td><td className="num">{w.visits}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <ReportDownload
            url={`/clubs/${clubSlug}/membership/report`}
            params={{ from: from || undefined, to: to || undefined }}
            fallbackName="membership-gate"
            csvSheets={[{ index: 0, label: 'CSV — gate' }, { index: 1, label: 'CSV — bar' }]}
            note="Excel has the gate and bar sheets plus a note on how the numbers are counted. Leave the dates blank for everything on record."
          />
        </>
      )}
    </div>
  );
}
